import { useMemo } from 'react';
// @mui
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
// components
import Iconify from '../../../components/Iconify';
import {
  LinkKind,
  LinkOption,
  addId,
  addableOptions,
  buildRows,
  moveId,
  optionText,
  removeId,
} from './diplomaLinks';

// ----------------------------------------------------------------------

// The courses (or tracks) of a diploma as rows in STORED order — the order
// students see (diploma-courses-tracks-multiselect D1/D3). A linked item is
// never dropped because the options lack it: inactive, partner and deleted
// items stay as tagged rows. ↑ / ↓ move, ✕ removes, "Add a course…" appends.

type Props = {
  kind: LinkKind;
  label: string;
  helper?: string;
  addLabel: string;
  ids: string[];
  options: LinkOption[];
  // The diploma's populated refs: labels for an item the options lack.
  populated?: unknown[];
  readOnly?: boolean;
  loading?: boolean;
  // The rows differ from the loaded ones: show "Unsaved changes — Undo".
  dirty?: boolean;
  onChange(ids: string[]): void;
  onUndo?(): void;
};

const TAG_COLOR = { Inactive: 'warning', Partner: 'info', 'Not found': 'error' } as const;

export default function LinkedItemsField({
  kind,
  label,
  helper,
  addLabel,
  ids,
  options,
  populated,
  readOnly = false,
  loading = false,
  dirty = false,
  onChange,
  onUndo,
}: Props) {
  const rows = useMemo(
    () => buildRows(kind, ids, options, populated),
    [kind, ids, options, populated]
  );
  const addable = useMemo(() => addableOptions(options, ids), [options, ids]);
  const testId = `linked-${kind}`;

  return (
    <Stack spacing={1} data-testid={testId}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2">{label}</Typography>
        {rows.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            Order students see
          </Typography>
        )}
      </Stack>
      {helper && (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      )}

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid={`${testId}-empty`}>
          None linked.
        </Typography>
      ) : (
        <Box
          component="ol"
          sx={{
            m: 0,
            p: 0,
            listStyle: 'none',
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 1,
          }}
        >
          {rows.map((row, i) => (
            <Box
              component="li"
              key={row.id}
              data-testid={`${testId}-row`}
              data-id={row.id}
              sx={{
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderTop: (t) => (i ? `1px solid ${t.palette.divider}` : 'none'),
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 20 }}>
                {i + 1}.
              </Typography>
              <Typography
                variant="body2"
                sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
                data-testid={`${testId}-label`}
              >
                {row.label}
              </Typography>
              {row.tags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  variant="outlined"
                  color={TAG_COLOR[tag]}
                  label={tag}
                />
              ))}
              {!readOnly && (
                <Stack direction="row" spacing={0}>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Move ${row.label} up`}
                        disabled={i === 0}
                        onClick={() => onChange(moveId(ids, row.id, -1))}
                      >
                        <Iconify icon="eva:arrow-upward-fill" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Move ${row.label} down`}
                        disabled={i === rows.length - 1}
                        onClick={() => onChange(moveId(ids, row.id, 1))}
                      >
                        <Iconify icon="eva:arrow-downward-fill" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove from this diploma">
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Remove ${row.label}`}
                      onClick={() => onChange(removeId(ids, row.id))}
                    >
                      <Iconify icon="eva:close-fill" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Box>
          ))}
        </Box>
      )}

      {dirty && !readOnly && (
        <Stack direction="row" alignItems="center" spacing={1} data-testid={`${testId}-dirty`}>
          <Typography variant="caption" color="warning.main">
            Unsaved changes
          </Typography>
          {onUndo && (
            <Typography
              component="button"
              type="button"
              variant="caption"
              onClick={onUndo}
              sx={{
                border: 0,
                p: 0,
                bgcolor: 'transparent',
                color: 'primary.main',
                cursor: 'pointer',
              }}
            >
              Undo
            </Typography>
          )}
        </Stack>
      )}

      {!readOnly && !loading && (
        <Autocomplete
          size="small"
          options={addable}
          value={null}
          blurOnSelect
          clearOnBlur
          getOptionLabel={(o: LinkOption) => optionText(kind, o)}
          isOptionEqualToValue={(o: LinkOption, v: LinkOption) => o._id === v._id}
          onChange={(_e, picked: LinkOption | null) => {
            if (picked) onChange(addId(ids, String(picked._id)));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={addLabel}
              placeholder="Type to search"
              inputProps={{ ...params.inputProps, 'data-testid': `${testId}-add` }}
            />
          )}
        />
      )}
    </Stack>
  );
}
